import { Link } from 'office-ui-fabric-react';
import * as React from 'react';
import { connect } from 'react-redux';
import { post } from '../../../fetch-interceptor';
import { ApplicationState } from '../../../store';
import { catchApiError } from '../../../store/utils';
import PostMessageReceiver from '../PostMessageReceiver';
import { InfoBlock } from './InfoBlock';
import { oauthTarget } from './JiraConnectionEdit';
import { JiraOAuthState } from '../../../store/integration/JiraStore';

const OAUTH_ACCESS_GRANT_FINISHED_CHECK_INTERVAL = 500;

type RequestToken = {
    oAuthToken: string;
    oAuthTokenSecret: string;
}

type AccessToken = {
    oAuthToken: string;
    oAuthTokenSecret: string;
    oAuthExpiresIn: number;
    oAuthSessionHandle: string;
    oAuthAuthorizationExpiresIn: number;
}

const OAuthConfig = {
    getAppRegistrationUrl: (url: string) => `${url}/plugins/servlet/applinks/listApplicationLinks`,
    ApplicationUrl: `https://${window.location.host}`,
    ApplicationName: 'PPM Express App',
    getOAuthTokenUrl: (url: string, oAuthToken: string) => `${url}/plugins/servlet/oauth/authorize?oauth_token=${oAuthToken}`,
    RequestTokenUrl: '/api/integration/jira/auth/oauth/request-token',
    AccessTokenUrl: '/api/integration/jira/auth/oauth/access-token'
}

type OAuth1AuthControlProps = {
    url: string;
    oauth: JiraOAuthState;
    setError: (error?: string) => void;
    onSuccess: (data: { oAuthToken: string; oAuthTokenSecret: string; oAuthSessionHandle: string }) => void;
}

export const OAuth1AuthControl = connect((state: ApplicationState) => ({ oauth: state.jira.oauth }))((props: OAuth1AuthControlProps) => {
    const [isOAuthProcessing, setIsOAuthProcessing] = React.useState(false);
    const [showAppRegistrationInfo, setShowAppRegistrationInfo] = React.useState(false);
    const { url, oauth, setError, onSuccess } = props;

    const onAuthFailure = (reason: any) => {
        catchApiError(_ => {
            setIsOAuthProcessing(false);
            setError(_);
        })(reason);
    };

    const postAccessToken = (wndw: Window, token: RequestToken) => {
        const timer = setInterval(() => {
            if (wndw.closed) {
                clearInterval(timer);

                const oauthData = {
                    url: url,
                    oAuthToken: token.oAuthToken,
                    oAuthTokenSecret: token.oAuthTokenSecret
                };

                post<AccessToken>(OAuthConfig.AccessTokenUrl, oauthData)
                    .then(data => {
                        setIsOAuthProcessing(false);
                        setError(undefined);
                        onSuccess(data);
                    })
                    .catch(onAuthFailure);
            }
        }, OAUTH_ACCESS_GRANT_FINISHED_CHECK_INTERVAL);
    }

    const onAuthAuthorize = (): void => {
        post<RequestToken>(OAuthConfig.RequestTokenUrl, { url: url })
            .then((token: RequestToken) => {
                const window = PostMessageReceiver
                    .openPopupWindow(OAuthConfig.getOAuthTokenUrl(url, token.oAuthToken), oauthTarget, { width: 700, height: 450 });
                if (window) {
                    postAccessToken(window, token);
                }
            })
            .catch(onAuthFailure);

        setIsOAuthProcessing(true);
        setError(undefined);
    };

    return <>
        <div className="description">
            If you already registered your Jira app,
            please <Link className="link" disabled={isOAuthProcessing} onClick={onAuthAuthorize}>Authorize</Link>.
            If you don't -
            click <Link className="link" disabled={isOAuthProcessing} onClick={() => setShowAppRegistrationInfo(true)}>App Registration</Link> first.
        </div>
        {showAppRegistrationInfo && <div className="oauth-info">
            <div className="title">App Registration</div>
            <div className="description">
                Please follow
                the <Link target="_blank" href={OAuthConfig.getAppRegistrationUrl(url)}>link</Link> and
                register Jira app using the following app registration details
            </div>
            {<InfoBlock name="Step 1" lines={[
                { name: 'Application URL', value: OAuthConfig.ApplicationUrl },
                { name: 'Application Name', value: OAuthConfig.ApplicationName },
                { name: 'Create incoming link', value: "Yes", hideCopy: true }
            ]} />}
            {<InfoBlock name="Step 2" lines={[
                { name: 'Consumer Key', value: oauth.consumerKey },
                { name: 'Consumer Name', value: oauth.consumerName },
                { name: 'Public Key', value: oauth.publicKey }
            ]} />}
        </div>}
    </>;
});
